import styled from "styled-components";
import { RATypography } from "@/components/customs";
import { useTrackVisibility } from "react-intersection-observer-hook";

export function PrivacyPolicyContents() {
	const [ref] = useTrackVisibility();

	const _renderIntroduction = () => {
		return (
			<>
				<Content>
					<RATypography variant="h3">Introduction</RATypography>
				</Content>
				<RATypography variant="body3" color="#595959" top={20}>
					Thank you for choosing to be part of our community at Royal Air Charter (
					<b style={{ color: "#222222" }}>“company”, “we”, “us”, or “our”</b>
					). We are committed to protecting your personal information and your right to privacy. If you have
					any questions or concerns about our policy, or our practices with regards to your personal
					information, please contact us at info@royalair.com.
				</RATypography>
				<RATypography variant="body3" color="#595959" top={20}>
					When you visit our website https://www.royalair.com (<b style={{ color: "#222222" }}>“Site”</b>) and
					use our services, you trust us with your personal information. We take your privacy very seriously.
					In this privacy notice, we describe our privacy policy. We seek to explain to you in the clearest
					way possible what information we collect, how we use it and what rights you have in relation to it.
					We hope you take some time to read through it carefully, as it is important. If there are any terms
					in this privacy policy that you do not agree with, please discontinue use of our site and our
					services.
				</RATypography>
				<RATypography variant="body3" color="#595959" top={20}>
					This privacy policy applies to all information collected through our website (such as{" "}
					<b style={{ color: "#222222" }}>https://www.royalair.com</b>
					), and/or any related services, sales, marketing or events (we refer to them collectively in this
					privacy policy as the
					<b style={{ color: "#222222" }}>“Sites“</b>).
				</RATypography>
				<RATypography variant="body3" color="#595959" top={20}>
					<i>
						Please read this privacy policy carefully as it will help you make informed decisions about
						sharing your personal information with us.
					</i>
				</RATypography>
			</>
		);
	};

	const _renderInformationWeCollect = () => {
		return (
			<>
				<Content>
					<RATypography variant="h3">What Information Do We Collect?</RATypography>
				</Content>
				<RATypography variant="sub-heading3" top={20}>
					Personal Information You Disclose To Us
				</RATypography>
				<RATypography variant="body3" color="#595959" top={10}>
					<i>
						In Short: We collect personal information that you provide to us such as name, address, contact
						information, passwords and security data.
					</i>
				</RATypography>
				<RATypography variant="body3" color="#595959" top={10}>
					We collect personal information that you voluntarily provide to us when [registering on the Sites,]
					expressing an interest in obtaining information about us or our products and services, when
					participating in activities on the Sites (such as requesting a quote in our customer portal or
					creating Jet 1 suite bookings) or otherwise contacting us.
				</RATypography>
				<RATypography variant="body3" color="#595959" top={10}>
					The personal information that we collect depends on the context of your interactions with us and the
					Sites, the choices you make and the products and features you use. The personal information we
					collect can include the following:
				</RATypography>

				<RATypography variant="sub-heading3" top={20}>
					Name and Contact Data
				</RATypography>
				<RATypography variant="body3" color="#595959" top={10}>
					We collect your first and last name, email address, postal address, phone number, and other similar
					contact data.
				</RATypography>

				<RATypography variant="sub-heading3" top={20}>
					Credentials
				</RATypography>
				<RATypography variant="body3" color="#595959" top={10}>
					We collect passwords, password hints, and similar security information used for authentication and
					account access. All personal information that you provide to us must be true, complete and accurate,
					and you must notify us of any changes to such personal information.
				</RATypography>

				<RATypography variant="sub-heading3" top={20}>
					Information Automatically Collected
				</RATypography>
				<RATypography variant="body3" color="#595959" top={10}>
					<i>
						In Short: Some information – such as IP address and/or browser and device characteristics – is
						collected automatically when you visit our websites.
					</i>
				</RATypography>
				<RATypography variant="body3" color="#595959" top={10}>
					We automatically collect certain information when you visit, use or navigate the Sites. This
					information does not reveal your specific identity <i>(like your name or contact information)</i>{" "}
					but may include device and usage information, such as your IP address, browser and device
					characteristics, operating system, language preferences, referring URLs, device name, country,
					location, information about how and when you use our Site and other technical information. This
					information is primarily needed to maintain the security and operation of our Sites, and for our
					internal analytics and reporting purposes.
				</RATypography>
				<RATypography variant="body3" color="#595959" top={10}>
					Like many businesses, we also collect information through cookies and similar technologies. You can
					find out more about this in our Cookies Policy
					<br />
					<i>https://www.royalair.com/cookie-policy</i>
				</RATypography>
			</>
		);
	};

	const _renderHowUserYourInfo = () => {
		return (
			<>
				<Content>
					<RATypography variant="h3">How Do We Use Your Information?</RATypography>
				</Content>
				<RATypography variant="body3" color="#595959" top={20}>
					<i>
						In Short: We process your information for purposes based on legitimate business interests, the
						fulfillment of our contract with you, compliance with our legal obligations, and/or your
						consent.
					</i>
				</RATypography>
				<RATypography variant="body3" color="#595959" top={20}>
					We use personal information collected via our Sites for a variety of business purposes described
					below. We process your personal information for these purposes in reliance on our legitimate
					business interests (<b style={{ color: "#222222" }}>“Business Purposes”</b>), in order to enter into
					or perform a contract with you (<b style={{ color: "#222222" }}>“Contractual”</b>), with your
					consent (<b style={{ color: "#222222" }}>“Consent”</b>
					), and/or for compliance with our legal obligations (
					<b style={{ color: "#222222" }}>“Legal Reasons”</b>). We indicate the specific processing grounds we
					rely on next to each purpose listed below.
				</RATypography>
				<RATypography variant="body3" color="#595959" top={20}>
					We use the information we collect or receive:
				</RATypography>
				<RATypographyCustom>
					<li>
						To send you marketing and promotional communications [for Business Purposes and/or with your
						Consent]. We and/or our third party marketing partners may use the personal information you send
						to us for our marketing purposes, if this is in accordance with your marketing preferences. You
						can opt-out of our marketing emails at any time (see the{" "}
						<b style={{ color: "#222222" }}>“Your Privacy Rights”</b> below).
					</li>
					<li style={{ marginTop: "12px" }}>
						To send administrative information to you [for Business Purposes, Legal Reasons and/or possibly
						Contractual]. We may use your personal information to send you product, service and new feature
						information and/or information about changes to our terms, conditions, and policies.
					</li>
					<li style={{ marginTop: "12px" }}>
						Fulfill and manage your orders or bookings [for Contractual reasons]. We may use your
						information to fulfill and manage your orders, bookings, payments, and exchanges made through
						the Sites.
					</li>
					<li style={{ marginTop: "12px" }}>
						Request Feedback [for our Business Purposes and/or with your Consent]. We may use your
						information to request feedback and to contact you about your use of our Sites and services.
					</li>
					<li style={{ marginTop: "12px" }}>
						To protect our Sites [for Business Purposes and/or Legal Reasons]. We may use your information
						as part of our efforts to keep our Sites safe and secure (for example, for fraud monitoring and
						prevention).
					</li>
					<li style={{ marginTop: "12px" }}>
						To enable user-to-user communications [with your consent]. We may use your information in order
						to enable user-to-user communications with each user’s consent.
					</li>
					<li style={{ marginTop: "12px" }}>
						To enforce our terms, conditions and policies [for Business Purposes, Legal Reasons and/or
						possibly Contractual].
					</li>
					<li style={{ marginTop: "12px" }}>
						To respond to legal requests and prevent harm [for Legal Reasons]. If we receive a subpoena or
						other legal request, we may need to inspect the data we hold to determine how to respond.
					</li>
					<li style={{ marginTop: "12px" }}>
						For other Business Purposes. We may use your information for other Business Purposes, such as
						data analysis, identifying usage trends, determining the effectiveness of our promotional
						campaigns and to evaluate and improve our Sites, products, services, marketing and your
						experience.
					</li>
				</RATypographyCustom>
			</>
		);
	};

	const _renderShareInformation = () => {
		return (
			<>
				<Content>
					<RATypography variant="h3">Will Your Information Be Shared With Anyone?</RATypography>
				</Content>
				<RATypography variant="body3" color="#595959" top={20}>
					<i>
						In Short: We only share information with your consent, to comply with laws, to protect your
						rights, or to fulfill business obligations.
					</i>
				</RATypography>
				<RATypography variant="body3" color="#595959" top={20}>
					We only share and disclose your information in the following situations:
				</RATypography>
				<RATypographyCustom>
					<li>
						Compliance with Laws. We may disclose your information where we are legally required to do so in
						order to comply with applicable law, governmental requests, a judicial proceeding, court order,
						or legal process, such as in response to a court order or a subpoena (including in response to
						public authorities to meet national security or law enforcement requirements).
					</li>
					<li style={{ marginTop: "12px" }}>
						Vital Interests and Legal Rights. We may disclose your information where we believe it is
						necessary to investigate, prevent, or take action regarding potential violations of our
						policies, suspected fraud, situations involving potential threats to the safety of any person
						and illegal activities, or as evidence in litigation in which we are involved.
					</li>
					<li style={{ marginTop: "12px" }}>
						Vendors, Consultants and Other Third-Party Service Providers. We may share your data with third
						party vendors, service providers, contractors, agents operators, brokers or other associated
						with the Royal Air websites who perform services for us or on our behalf and require access to
						such information to do that work. Examples include: payment processing, data analysis, email
						delivery, hosting services, customer service, flight bookings, marketing efforts, or in relation
						to any other type of purchase made through our Royal Air website. We may allow selected third
						parties to use tracking technology on the Sites, which will enable them to collect data about
						how you interact with the Sites over time. This information may be used to, among other things,
						analyze and track data, determine the popularity of certain content and better understand online
						activity. Unless described in this Policy, we do not share, sell, rent or trade any of your
						information with third parties for their promotional purposes.
					</li>
					<li style={{ marginTop: "12px" }}>
						Business Transfers. We may share or transfer your information in connection with, or during
						negotiations of, any merger, sale of company assets, financing, or acquisition of all or a
						portion of our business to another company.
					</li>
					<li style={{ marginTop: "12px" }}>
						Affiliates. We may share your information with our affiliates, in which case we will require
						those affiliates to honor this privacy policy. Affiliates include our parent company and any
						subsidiaries, joint venture partners or other companies that we control or that are under common
						control with us.
					</li>
					<li style={{ marginTop: "12px" }}>
						Business Partners. We may share your information with our business partners to offer you certain
						products, services or promotions.
					</li>
					<li style={{ marginTop: "12px" }}>
						With your Consent. We may disclose your personal information for any other purpose with your
						consent.
					</li>
				</RATypographyCustom>
			</>
		);
	};

	const _renderUserGoogleMap = () => {
		return (
			<>
				<Content>
					<RATypography variant="h3">Do We Use Google Maps?</RATypography>
				</Content>
				<RATypography variant="body3" color="#595959" top={20}>
					<i>In Short: Yes, we use Google Maps for the purpose of providing better service.</i>
				</RATypography>
				<RATypography variant="body3" color="#595959" top={20}>
					This website, or mobile application, uses Google Maps APIs. You may find the Google Maps APIs Terms
					of Service{" "}
					<b style={{ color: "#222222" }}>
						<u> https://developers.google.com/maps/term</u>
					</b>
					. To better understand Google’s Privacy Policy, please refer to{" "}
					<b style={{ color: "#222222" }}>
						<u>https://www.google.com/policies/privacy</u>
					</b>
					.
				</RATypography>
				<RATypography variant="body3" color="#595959" top={20}>
					By using our Maps API Implementation, you agree to be bound by Google’s Terms of Service. [By using
					our implementation of the Google Maps APIs, you agree to allow us to gain access to information
					about you including personally identifiable information (such as usernames) and non-personally
					identifiable information (such as location).
				</RATypography>
				<RATypography variant="body3" color="#595959" top={20}>
					For a full list of what we use information for, please see the previous sections titled{" "}
					<b style={{ color: "#222222" }}>“Use of Your Information”</b> and{" "}
					<b style={{ color: "#222222" }}>“Disclosure of Your Information”</b>. You agree to allow us to
					obtain or cache your location. You may revoke your consent at anytime. We use information about
					location in conjunction with data from other data providers.
				</RATypography>
			</>
		);
	};

	const _renderInforTransferredInternationally = () => {
		return (
			<>
				<Content>
					<RATypography variant="h3">Is Your Information Transferred Internationally?</RATypography>
				</Content>
				<RATypography variant="body3" color="#595959" top={20}>
					<i>
						In Short: We may transfer, store, and process your information in countries other than your own.
					</i>
				</RATypography>
				<RATypography variant="body3" color="#595959" top={20}>
					Our servers are located in the United States of America. If you are accessing our Sites from outside
					The United States of America, please be aware that your information may be transferred to, stored,
					and processed by us in our facilities and by those third parties with whom we may share your
					personal information (see <b style={{ color: "#222222" }}>“Disclosure of Your Information”</b>{" "}
					above), in The United States of America, and other countries.
				</RATypography>
				<RATypography variant="body3" color="#595959" top={20}>
					If you are a resident in the European Economic Area, then these countries may not have data
					protection or other laws as comprehensive as those in your country. We will however take all
					necessary measures to protect your personal information in accordance with this privacy policy and
					applicable law.
				</RATypography>
				<RATypography variant="body3" color="#595959" top={20}>
					EU-U.S. Privacy Shield Framework: In particular Royal Air Charter complies with the EU-U.S. Privacy
					Shield Framework as set forth by the U.S. Department of Commerce regarding the collection, use, and
					retention of personal information transferred from the European Union to the United States and has
					certified its compliance with it. As such, Royal Air Charter is committed to subjecting all personal
					information received from European Union (EU) member countries, in reliance on the Privacy Shield
					Framework, to the Framework’s applicable Principles. To learn more about the Privacy Shield
					Framework, visit the{" "}
					<b style={{ color: "#222222" }}>
						<u>U.S. Department of Commerce’s Privacy Shield List</u>
					</b>
					.
				</RATypography>
				<RATypography variant="body3" color="#595959" top={20}>
					Royal Air Charter is responsible for the processing of personal information it receives, under the
					Privacy Shield Framework, and subsequently transfers to a third party acting as an agent on its
					behalf.
				</RATypography>
				<RATypography variant="body3" color="#595959" top={20}>
					With respect to personal information received or transferred pursuant to the Privacy Shield
					Framework, Royal Air Charter, is subject to the regulatory enforcement powers of the U.S. FTC. In
					certain situations, we may be required to disclose personal information in response to lawful
					requests by public authorities, including to meet national security or law enforcement requirements.
				</RATypography>
			</>
		);
	};

	const _renderThirdPartyWebsite = () => {
		return (
			<>
				<Content>
					<RATypography variant="h3">What Is Our Stance On Third-Party Websites?</RATypography>
				</Content>
				<RATypography variant="body3" color="#595959" top={20}>
					<i>
						In Short: We are not responsible for the safety of any information that you share with
						third-party providers who are linked via our sites, or sell services or products using any of
						our platforms such as <b style={{ color: "#222222" }}>https://www.royalair.com</b>, but are not
						affiliated with, our websites.{" "}
					</i>
				</RATypography>
				<RATypography variant="body3" color="#595959" top={20}>
					Our Sites and applications may contain links to third parties that are not affiliated with us and
					which may link to other websites, online services or mobile applications. We cannot guarantee the
					safety and privacy of data you provide to any third parties. Any data collected by third parties is
					not covered by this privacy policy. We are not responsible for the content or privacy and security
					practices and policies of any third parties, including other websites, services or applications that
					may be linked to or from the Sites. You should review the policies of such third parties and contact
					them directly to respond to your questions.
				</RATypography>
			</>
		);
	};

	const _renderHowLongWeKeepInformation = () => {
		return (
			<>
				<Content>
					<RATypography variant="h3">How Long Do We Keep Your Information?</RATypography>
				</Content>
				<RATypography variant="body3" color="#595959" top={20}>
					<i>
						In Short: We keep your information for as long as necessary to fulfill the purposes outlined in
						this privacy policy unless otherwise required by law.
					</i>
				</RATypography>
				<RATypography variant="body3" color="#595959" top={20}>
					We will only keep your personal information for as long as it is necessary for the purposes set out
					in this privacy policy, unless a longer retention period is required or permitted by law (such as
					tax, accounting or other legal requirements). No purpose in this policy will require us keeping your
					personal information for longer than 2 years past the termination of your account.
				</RATypography>
				<RATypography variant="body3" color="#595959" top={20}>
					When we have no ongoing legitimate business need to process your personal information, we will
					either delete or anonymize it, or, if this is not possible (for example, because your personal
					information has been stored in backup archives), then we will securely store your personal
					information and isolate it from any further processing until deletion is possible.
				</RATypography>
			</>
		);
	};

	const _renderHowKeepYourInfoSafe = () => {
		return (
			<>
				<Content>
					<RATypography variant="h3">How Do We Keep Your Information Safe?</RATypography>
				</Content>
				<RATypography variant="body3" color="#595959" top={20}>
					<i>
						In Short: We aim to protect your personal information through a system of organizational and
						technical security measures.
					</i>
				</RATypography>
				<RATypography variant="body3" color="#595959" top={20}>
					We have implemented appropriate technical and organizational security measures designed to protect
					the security of any personal information we process. However, please also remember that we cannot
					guarantee that the internet itself is 100% secure. Although we will do our best to protect your
					personal information, transmission of personal information to and from our Sites is at your own
					risk. You should only access the services within a secure environment.
				</RATypography>
			</>
		);
	};

	const _renderHowCollectInfoFromMinors = () => {
		return (
			<>
				<Content>
					<RATypography variant="h3">Do We Collect Information From Minors?</RATypography>
				</Content>
				<RATypography variant="body3" color="#595959" top={20}>
					<i>In Short: We do not knowingly collect data from or market to children under 18 years of age.</i>
				</RATypography>
				<RATypography variant="body3" color="#595959" top={20}>
					We do not knowingly solicit data from or market to children under 18 years of age. By using the
					Sites and apps, you represent that you are at least 18 or that you are the parent or guardian of
					such a minor and consent to such minor dependent’s use of the Site and App. If we learn that
					personal information from users less than 18 years of age has been collected, we will deactivate the
					account and take reasonable measures to promptly delete such data from our records. The only time
					such data may be collected is in association with a parental account for purposes of flight
					bookings, such as passports or other travel documents necessary for travel in accordance to
					international laws and regulation. If you become aware of any data we have collected from children
					under age 18, please contact us at{" "}
					<b style={{ color: "#222222" }}>
						<u>info@royalair.com</u>
					</b>
					.
				</RATypography>
			</>
		);
	};

	const _renderWhatAreYourPrivacyRights = () => {
		return (
			<>
				<Content>
					<RATypography variant="h3">What Are Your Privacy Rights?</RATypography>
				</Content>
				<RATypography variant="body3" color="#595959" top={20}>
					<i>
						In Short: In some regions, such as the European Economic Area, you have rights that allow you
						greater access to and control over your personal information. You may review, change, or
						terminate your account at any time.
					</i>
				</RATypography>
				<RATypography variant="body3" color="#595959" top={20}>
					In some regions (like the European Economic Area), you have certain rights under applicable data
					protection laws. These may include the right (i) to request access and obtain a copy of your
					personal information, (ii) to request rectification or erasure; (iii) to restrict the processing of
					your personal information; and (iv) if applicable, to data portability. In certain circumstances,
					you may also have the right to object to the processing of your personal information. To make such a
					request, please use the contact details provided below or email us at info@royalair.com. We will
					consider and act upon any request in accordance with applicable data protection laws.
				</RATypography>
				<RATypography variant="body3" color="#595959" top={20}>
					If we are relying on your consent to process your personal information, you have the right to
					withdraw your consent at any time. Please note however that this will not affect the lawfulness of
					the processing before its withdrawal.
				</RATypography>
				<RATypography variant="body3" color="#595959" top={20}>
					If you are a resident in the European Economic Area and you believe we are unlawfully processing
					your personal information, you also have the right to complain to your local data protection
					supervisory authority. You can find their contact details here:{" "}
					<b style={{ color: "#222222" }}>
						<u>http://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm</u>
					</b>
				</RATypography>

				<RATypography variant="sub-heading3" color="#222222" top={20}>
					Account Information
				</RATypography>
				<RATypography variant="body3" color="#595959" top={10}>
					You may at any time review or change the information in your account or terminate your account by:
				</RATypography>
				<RATypographyCustom>
					<li>Logging into your account settings and updating your account</li>
					<li style={{ marginTop: "10px" }}>Contacting us using the contact information provided below</li>
				</RATypographyCustom>
				<RATypography variant="body3" color="#595959" top={10}>
					Upon your request to terminate your account, we will deactivate or delete your account and
					information from our active databases. However, some information may be retained in our files to
					prevent fraud, troubleshoot problems, assist with any investigations, enforce our Terms of Use
					and/or comply with legal requirements.]
				</RATypography>
				<RATypography variant="body3" color="#595959" top={10}>
					Cookies and similar technologies: Most Web browsers are set to accept cookies by default. If you
					prefer, you can usually choose to set your browser to remove cookies and to reject cookies. If you
					choose to remove cookies or reject cookies, this could affect certain features or services of our
					Sites. For further information, please see our Cookie Policy{" "}
					<b style={{ color: "#222222" }}>
						<u>https://www.royalair.com/cookie-policy</u>
					</b>
				</RATypography>
				<RATypography variant="body3" color="#595959" top={10}>
					Opting out of email marketing: You can unsubscribe from our marketing email list at any time by
					clicking on the unsubscribe link in the emails that we send or by contacting us using the details
					provided below. You will then be removed from the marketing email list – however, we will still need
					to send you service-related emails that are necessary for the administration and use of your
					account. You can also opt-out by:
				</RATypography>
				<RATypographyCustom>
					<li>Noting your preferences at the time you register your account with the Sites.</li>
					<li style={{ marginTop: "10px" }}>
						Logging into your account settings and updating your preferences.
					</li>
					<li style={{ marginTop: "10px" }}>Contacting us using the contact information provided below</li>
				</RATypographyCustom>
			</>
		);
	};

	const _renderSpecificPrivacyRights = () => {
		return (
			<>
				<Content>
					<RATypography variant="h3">Do California Residents Have Specific Privacy Rights?</RATypography>
				</Content>
				<RATypography variant="body3" color="#595959" top={20}>
					<i>
						In Short: Yes, if you are a resident of California, you are granted specific rights regarding
						access to your personal information.
					</i>
				</RATypography>
				<RATypography variant="body3" color="#595959" top={20}>
					California Civil Code Section 1798.83, also known as the{" "}
					<b style={{ color: "#222222" }}>“Shine The Light”</b> law, permits our users who are California
					residents to request and obtain from us, once a year and free of charge, information about
					categories of personal information (if any) we disclosed to third parties for direct marketing
					purposes and the names and addresses of all third parties with which we shared personal information
					in the immediately preceding calendar year. If you are a California resident and would like to make
					such a request, please submit your request in writing to us using the contact information provided
					below.
				</RATypography>
				<RATypography variant="body3" color="#595959" top={20}>
					If you are under 18 years of age, reside in California, and have a registered account with Royal Air
					Charter, our applications, or using our platform through a third party you have the right to request
					removal of unwanted data. To request removal of such data, please contact us using the contact
					information provided below, and include the email address associated with your account and a
					statement that you reside in California. We will make sure the data is not publicly displayed on the
					Royal Air Charter website, but please be aware that the data may not be completely or
					comprehensively removed from our systems.
				</RATypography>
			</>
		);
	};

	const _renderMakeUpdatePolicy = () => {
		return (
			<>
				<Content>
					<RATypography variant="h3">Do We Make Updates To This Policy?</RATypography>
				</Content>
				<RATypography variant="body3" color="#595959" top={20}>
					<i>In Short: Yes, we will update this policy as necessary to stay compliant with relevant laws.</i>
				</RATypography>
				<RATypography variant="body3" color="#595959" top={20}>
					We may update this privacy policy from time to time. The updated version will be indicated by an
					updated <b style={{ color: "#222222" }}>“Revised”</b> date and the updated version will be effective
					as soon as it is accessible. If we make material changes to this privacy policy, we may notify you
					either by prominently posting a notice of such changes or by directly sending you a notification. We
					encourage you to review this privacy policy frequently to be informed of how we are protecting your
					information.
				</RATypography>
			</>
		);
	};

	const _renderHowContactUsAboutPolicy = () => {
		return (
			<>
				<Content>
					<RATypography variant="h3">How Can You Contact Us About This Policy?</RATypography>
				</Content>
				<RATypography variant="body3" color="#595959" top={20}>
					If you have questions or comments about this policy, email us at{" "}
					<b style={{ color: "#222222" }}>
						<u>info@royalair.com</u>
					</b>{" "}
					or by post to:
				</RATypography>
				<RATypography
					variant="sub-heading3"
					style={{
						textTransform: "uppercase",
						fontWeight: 700,
						letterSpacing: 0.8,
					}}
					color="#222222"
					top={20}
				>
					Royal Air Charter
				</RATypography>
				<RATypography variant="body3" color="#595959">
					<i>2141 Airport Road</i>
					<br />
					<i>Waterford MI 48327 </i>
					<br />
					<i>United States of America</i>
				</RATypography>
				<RATypography variant="body3" color="#595959" top={20}>
					If you are a resident in the European Economic Area, the “data controller” of your personal
					information is Avian Solutions Pte. Ltd.. Avian Solutions Pte. Ltd., has appointed representative in
					the EEA. You can contact them directly regarding the processing of your information by Royal Air
					Charter, by email at{" "}
					<b style={{ color: "#222222" }}>
						<u>datacontroller@aviansolutions.aero</u>
					</b>
				</RATypography>
			</>
		);
	};

	return (
		<HomeContactContainer ref={ref}>
			<WrapperContent>{_renderIntroduction()}</WrapperContent>
			<WrapperContent>{_renderInformationWeCollect()}</WrapperContent>
			<WrapperContent>{_renderHowUserYourInfo()}</WrapperContent>
			<WrapperContent>{_renderShareInformation()}</WrapperContent>
			<WrapperContent>{_renderUserGoogleMap()}</WrapperContent>
			<WrapperContent>{_renderInforTransferredInternationally()}</WrapperContent>
			<WrapperContent>{_renderThirdPartyWebsite()}</WrapperContent>
			<WrapperContent>{_renderHowLongWeKeepInformation()}</WrapperContent>
			<WrapperContent>{_renderHowKeepYourInfoSafe()}</WrapperContent>
			<WrapperContent>{_renderHowCollectInfoFromMinors()}</WrapperContent>
			<WrapperContent>{_renderWhatAreYourPrivacyRights()}</WrapperContent>
			<WrapperContent>{_renderSpecificPrivacyRights()}</WrapperContent>
			<WrapperContent>{_renderMakeUpdatePolicy()}</WrapperContent>
			<WrapperContent>{_renderHowContactUsAboutPolicy()}</WrapperContent>
		</HomeContactContainer>
	);
}

const HomeContactContainer = styled.div`
	width: 100%;
	position: relative;
	display: flex;
	flex-direction: column;
	gap: 40px;
	border-radius: 12px;
`;
const WrapperContent = styled.div`
	display: flex;
	justify-content: flex-start;
	flex-direction: column;
`;
const Content = styled.div`
	padding: 0px 20px;
	flex-direction: column;
	align-items: center;
	border-left: 2px solid #ffb11a;
`;

const RATypographyCustom = styled.div`
	padding: 0;
	margin: 0;
	font-family: "DM Sans";
	font-weight: 400;
	font-size: 14px;
	line-height: 22px;
	color: #595959;
	margin-top: 20px;
	margin-left: 20px;
`;
